import React, { useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

function ModalBasic({
  children,
  id,
  title,
  modalOpen,
  setModalOpen,
  widthClass = 'max-w-lg'
}) {
  const modalRef = useRef(null);
  const readyRef = useRef(false);

  // Arm click-outside after a tick so the opening click doesn't immediately close
  useEffect(() => {
    if (!modalOpen) { readyRef.current = false; return; }
    const t = setTimeout(() => { readyRef.current = true; }, 100);
    const keyHandler = (e) => { if (e.key === 'Escape') setModalOpen(false); };
    window.addEventListener('keydown', keyHandler);
    return () => { clearTimeout(t); window.removeEventListener('keydown', keyHandler); };
  }, [modalOpen, setModalOpen]);

  const handleBackdropClick = useCallback((e) => {
    if (!readyRef.current) return;
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      setModalOpen(false);
    }
  }, [setModalOpen]);

  return createPortal(
    <AnimatePresence>
      {modalOpen && (
        <motion.div
          key={id || 'modal-basic'}
          className="fixed inset-0 z-[9999] overflow-hidden flex items-center justify-center px-4 sm:px-6 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
          role="dialog"
          aria-modal="true"
          initial={false}
          onClick={handleBackdropClick}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/50"
            style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          />
          {/* Dialog */}
          <motion.div
            ref={modalRef}
            className={`admin-theme relative rounded-xl shadow-xl overflow-auto ${widthClass} w-full max-h-[calc(100%-1rem)] gch-bg-card border border-[var(--admin-border)]`}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-[var(--admin-border)]">
              <div className="flex justify-between items-center">
                <div className="font-semibold text-lg tracking-tight gch-text-primary">{title}</div>
                <button
                  onClick={() => setModalOpen(false)}
                  className="p-2 rounded-lg transition-all duration-200 gch-input-ghost gch-text-secondary hover:gch-text-primary hover:bg-white/10"
                >
                  <div className="sr-only">Close</div>
                  <svg className="w-4 h-4 fill-current">
                    <path d="M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.415L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z" />
                  </svg>
                </button>
              </div>
            </div>
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

export default ModalBasic;
